import type { Ref } from "react";
import {
  blackoutRuns,
  type Placed,
  type Token,
} from "@/lib/tool-kit/poetry/blackout";

export type BarStyle = "solid" | "grey" | "outline";

/** Grid measures in SVG units. 15 px Courier is 9 px a column; 60 columns fill a Letter line. */
const COLUMN_WIDTH = 9;
const ROW_HEIGHT = 24;
const MARGIN = 36;
const FONT_SIZE = 15;
const BAR_TOP = 2;
const BAR_HEIGHT = 20;
const CREDIT_ROOM = 34;
const FONT = "'Courier New', Courier, monospace";
const INK = "#111111";

export function pageSize(columns: number, rows: number, hasCredit: boolean) {
  return {
    width: MARGIN * 2 + columns * COLUMN_WIDTH,
    height:
      MARGIN * 2 +
      Math.max(rows, 1) * ROW_HEIGHT +
      (hasCredit ? CREDIT_ROOM : 0),
  };
}

function barPaint(style: BarStyle) {
  if (style === "outline") {
    return { fill: "none", stroke: INK, strokeWidth: 1.5 };
  }
  return { fill: style === "grey" ? "#555555" : INK };
}

/**
 * The page as a self-contained SVG: white paper, the kept words in type and a bar over
 * every other stretch. Everything is an attribute, not a class, because the PNG download
 * rasterises it away from the page's stylesheet.
 */
export function BlackoutPage({
  columns,
  rows,
  tokens,
  items,
  kept,
  barStyle,
  credit,
  label,
  svgRef,
}: {
  columns: number;
  rows: number;
  tokens: readonly Token[];
  items: readonly Placed[];
  kept: ReadonlySet<number>;
  barStyle: BarStyle;
  credit: string;
  label: string;
  svgRef: Ref<SVGSVGElement>;
}) {
  const { width, height } = pageSize(columns, rows, credit.length > 0);
  const paint = barPaint(barStyle);
  const runs = blackoutRuns(items, kept);
  return (
    <svg
      aria-label={label}
      height={height}
      ref={svgRef}
      role="img"
      style={{ maxWidth: "100%", height: "auto" }}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill="#ffffff" height={height} width={width} x="0" y="0" />
      {runs.map((run) => (
        <rect
          height={BAR_HEIGHT}
          key={`bar-${run.row}-${run.col}`}
          rx="2"
          width={run.len * COLUMN_WIDTH}
          x={MARGIN + run.col * COLUMN_WIDTH}
          y={MARGIN + run.row * ROW_HEIGHT + BAR_TOP}
          {...paint}
        />
      ))}
      {items
        .filter((item) => kept.has(item.index))
        .map((item) => (
          <text
            fill={INK}
            fontFamily={FONT}
            fontSize={FONT_SIZE}
            key={`word-${item.index}`}
            lengthAdjust="spacingAndGlyphs"
            textLength={item.len * COLUMN_WIDTH}
            x={MARGIN + item.col * COLUMN_WIDTH}
            y={MARGIN + item.row * ROW_HEIGHT + 17}
          >
            {tokens[item.index].text}
          </text>
        ))}
      {credit ? (
        <text
          fill="#555555"
          fontFamily={FONT}
          fontSize="11"
          x={MARGIN}
          y={height - MARGIN - 14}
        >
          {credit}
        </text>
      ) : null}
    </svg>
  );
}
